import { useContext, useState } from 'react'
import Cookies from 'js-cookie'
import { SnackbarContext } from 'contexts/SnackbarContext'
import { useNavigate } from 'react-router-dom'
import { axios, clearAuthData } from 'api'
import { getAccountInfo, AccountInfo, UpdateAccountInfoParams } from 'api/auth'
import { cookieOptions } from 'utils/cookieOptions'
import { useTranslation } from 'react-i18next'

export type { AccountInfo, UpdateAccountInfoParams }

const useAuth = () => {
  const { t } = useTranslation()
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
    () => !!Cookies.get('accessToken')
  )
  const [accountInfo, setAccountInfo] = useState<AccountInfo | null>(null)
  const [isLoadingAccountInfo, setIsLoadingAccountInfo] =
    useState<boolean>(false)
  const { showMessage } = useContext(SnackbarContext)
  const navigate = useNavigate()
  const [isImpersonated, setIsImpersonated] = useState(false)
  const [impersonatedEmail, setImpersonatedEmail] = useState<string | null>(
    null
  )
  const [showPasswordChangeDialog, setShowPasswordChangeDialog] =
    useState(false)
  const [passwordChangeSession, setPasswordChangeSession] = useState<
    string | null
  >(null)
  const [passwordChangeUsername, setPasswordChangeUsername] = useState<
    string | null
  >(null)

  const signOut = () => {
    setIsAuthenticated(false)
    setIsImpersonated(false)
    setImpersonatedEmail(null)
    clearAuthData()
    navigate('/login')
  }

  const login = async (email: string, password: string) => {
    const response = await axios.post('/accounts/login', {
      email,
      password
    })
    if (response.status === 200) {
      const { challengeName, session, username, accessToken, refreshToken } =
        response.data

      if (challengeName === 'NEW_PASSWORD_REQUIRED') {
        setPasswordChangeSession(session)
        setPasswordChangeUsername(username)
        setShowPasswordChangeDialog(true)
        return
      }

      setIsAuthenticated(true)
      Cookies.set('accessToken', accessToken, cookieOptions)
      Cookies.set('refreshToken', refreshToken, cookieOptions)
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
    } else {
      const errorMessage =
        response?.data?.error || t('snackbar.general.unexpectedError')
      showMessage(errorMessage, 'error')
    }
  }

  const fetchAccountInfo = async () => {
    setIsLoadingAccountInfo(true)
    try {
      const result = await getAccountInfo()
      if (result.success) {
        setAccountInfo(result.data ?? null)
      } else {
        console.error('Failed to fetch account info.')
      }
    } finally {
      setIsLoadingAccountInfo(false)
    }
  }

  const updateAccountInfo = async (params: UpdateAccountInfoParams) => {
    const response = await axios.put('/accounts/update-account-info', params)
    if (response.status === 200) {
      const updatedAccountInfo = response.data
      setAccountInfo(prevAccountInfo =>
        prevAccountInfo
          ? { ...prevAccountInfo, ...updatedAccountInfo }
          : updatedAccountInfo
      )

      let messageKey = 'snackbar.auth.accountUpdated'
      if (params.phoneNumber !== undefined) {
        messageKey = 'snackbar.auth.phoneUpdated'
      } else if (params.companyName !== undefined) {
        messageKey = 'snackbar.auth.companyUpdated'
      } else if (
        params.firstName !== undefined ||
        params.lastName !== undefined
      ) {
        messageKey = 'snackbar.auth.nameUpdated'
      }
      showMessage(t(messageKey))
    } else {
      const errorMessage =
        response?.data?.error || t('snackbar.general.unexpectedError')
      showMessage(errorMessage, 'error')
    }
  }

  return {
    signOut,
    login,
    fetchAccountInfo,
    updateAccountInfo,
    isAuthenticated,
    accountInfo,
    isLoadingAccountInfo,
    isImpersonated,
    setIsImpersonated,
    impersonatedEmail,
    setImpersonatedEmail,
    showPasswordChangeDialog,
    setShowPasswordChangeDialog,
    passwordChangeSession,
    passwordChangeUsername
  }
}

export default useAuth
